<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import './styles/theme.css';
// Bare import for its side effects only — registers the calc-snapshot and
// description-change log listeners (see its own header comment) regardless
// of whether the History panel below has ever been opened.
import './setup_log.js';
import { useTheme } from './use_theme.js';
import { usePersisted } from './use_persisted.js';
import { usePanelRequests } from './use_panels.js';
import { useShared } from './state.js';
import { useSetups } from './setups.js';
import { useAuth } from './auth.js';
import CollapsiblePanel from './collapsable_panel.vue';
import { useHeightTransition } from './use_height_transition.js';
import ErrorPopups from './components/error_popups.vue';
import ComputingIndicator from './components/computing_indicator.vue';
import LoginSyncIndicator from './components/login_sync_indicator.vue';
import SetupDescriptionComponent from './components/setup_description.vue';
import SightSettingsComponent from './components/input_sight_settings.vue';
import MeasuredMarksComponent from './components/input_marks.vue';
import CalculatedComponent from './components/output.vue';
import GraphComponent from './components/graph.vue';
import TrajectoryComponent from './components/trajectory.vue';
import FullMarksComponent from './components/full_marks.vue';
import CutsTableComponent from './components/cuts_table.vue';
import SightTapeComponent from './components/sight_tape.vue';
import HistoryPanelComponent from './components/history_panel.vue';
import MarkComponent from './components/mark.vue';
import AccountWidget from './components/account_widget.vue';
import SetupSelect from './components/setup_select.vue';

const { themes, currentTheme, setTheme } = useTheme();
const { undo, redo, canUndo, canRedo } = useShared();
const { activeSetupId } = useSetups();
const { isLoggedIn } = useAuth();

// Keyboard shortcuts: Cmd/Ctrl+Z to undo, Cmd/Ctrl+Shift+Z or Ctrl+Y to redo.
// No phone/touch equivalent exists to hook — the Undo/Redo buttons in the
// top bar are the touch-friendly path and work identically on mobile.
function onGlobalKeydown(e) {
  if (!(e.metaKey || e.ctrlKey)) return;
  const key = e.key.toLowerCase();
  if (key === 'z' && !e.shiftKey) {
    e.preventDefault();
    undo();
  } else if ((key === 'z' && e.shiftKey) || key === 'y') {
    e.preventDefault();
    redo();
  }
}

onMounted(() => window.addEventListener('keydown', onGlobalKeydown));
onUnmounted(() => window.removeEventListener('keydown', onGlobalKeydown));

const panelComponents = {
  setup_description: SetupDescriptionComponent,
  sight_settings: SightSettingsComponent,
  measured_marks: MeasuredMarksComponent,
  single_mark: MarkComponent,
  calculated: CalculatedComponent,
  full_marks: FullMarksComponent,
  cuts_table: CutsTableComponent,
  sight_tape: SightTapeComponent,
  graph: GraphComponent,
  trajectory: TrajectoryComponent,
  history: HistoryPanelComponent,
};

const panelOpenState = usePersisted('panel-open-states', {});

const panels = ref([
  { id: 'setup_description', title: 'Setup Description', full_width: false, open: panelOpenState.value.setup_description ?? false },
  { id: 'sight_settings', title: 'Sight Settings', full_width: false, open: panelOpenState.value.sight_settings ?? true },
  { id: 'measured_marks', title: 'Measured Marks', full_width: false, open: panelOpenState.value.measured_marks ?? true },
  { id: 'single_mark',   title: 'Compute Mark',   full_width: false, open: true },
  { id: 'calculated',    title: 'Calculated',     full_width: false, open: panelOpenState.value.calculated ?? false },
  { id: 'full_marks',    title: 'Full Marks',      full_width: true,  open: panelOpenState.value.full_marks ?? false },
  { id: 'cuts_table',    title: 'Cuts Table',      full_width: true,  open: panelOpenState.value.cuts_table ?? false },
  { id: 'sight_tape',    title: 'Sight Tape',      full_width: true,  open: panelOpenState.value.sight_tape ?? false },
  { id: 'graph',         title: 'Graph',           full_width: true,  open: panelOpenState.value.graph ?? false },
  { id: 'trajectory',    title: 'Trajectory',      full_width: true,  open: panelOpenState.value.trajectory ?? false },
  // Logged-in only (see referencePanels below) — the log this shows lives
  // entirely server-side (see setup_log.js), so it has nothing to show for
  // a session that was never authenticated.
  { id: 'history',       title: 'History',         full_width: true,  open: panelOpenState.value.history ?? false },
]);

// Mirror just the {id, open} shape into the persisted map — panels carry
// extra fields (title, full_width) we don't want in localStorage.
watch(
  () => panels.value.map(p => ({ id: p.id, open: p.open })),
  (newStates) => {
    const stateMap = {};
    newStates.forEach(p => { stateMap[p.id] = p.open; });
    panelOpenState.value = stateMap;
  },
  { deep: true }
);

const NON_REFERENCE_IDS = ['setup_description', 'sight_settings', 'measured_marks', 'single_mark'];

// setup_description is logged-in only too — its own edit history lives in
// the History panel's log (also logged-in only, see referencePanels
// below), so there's nowhere for a logged-out edit to go once made.
const setupPanels = computed(() =>
  panels.value.filter(p =>
    (p.id === 'sight_settings' || p.id === 'measured_marks') ||
    (p.id === 'setup_description' && isLoggedIn.value)
  )
);

const primaryPanel = computed(() =>
  panels.value.find(p => p.id === 'single_mark')
);

// The History panel's log lives entirely server-side (see setup_log.js) —
// nothing to show for a session that was never logged in, so it's filtered
// out of the button bar entirely rather than shown disabled/empty.
const referencePanels = computed(() =>
  panels.value.filter(p => !NON_REFERENCE_IDS.includes(p.id) && (p.id !== 'history' || isLoggedIn.value))
);

// Setup and reference panels are both accordions (see ACCORDION_GROUPS
// below), so at most one panel per group is ever open — a single nullable
// computed rather than a filtered array, so the template can swap it
// straight into a <Transition> for the open/close-and-swap animation.
const openSetupPanel = computed(() =>
  setupPanels.value.find(p => p.open) ?? null
);

const openReferencePanel = computed(() =>
  referencePanels.value.find(p => p.open) ?? null
);

const { beforeEnter, enter, afterEnter, beforeLeave, leave, afterLeave } = useHeightTransition();

// Panels that act as an accordion — opening one in a group closes any
// other open panel in that same group. Setup panels (Setup Description /
// Sight Settings / Measured Marks) are their own group, independent of the
// reference panels' group; Compute Mark is in neither since it's always
// open and not toggleable.
const ACCORDION_GROUPS = [
  ['setup_description', 'sight_settings', 'measured_marks'],
  ['calculated', 'full_marks', 'cuts_table', 'sight_tape', 'graph', 'trajectory', 'history'],
];

function accordionGroupFor(id) {
  return ACCORDION_GROUPS.find(group => group.includes(id));
}

(function enforceSingleOpenPerAccordionGroup() {
  for (const group of ACCORDION_GROUPS) {
    let keptOneOpen = false;
    for (const p of panels.value) {
      if (!group.includes(p.id) || !p.open) continue;
      if (keptOneOpen) p.open = false;
      else keptOneOpen = true;
    }
  }
})();

function togglePanel(id) {
  const panel = panels.value.find(p => p.id === id);
  if (!panel) return;

  const group = accordionGroupFor(id);
  if (group && !panel.open) {
    panels.value.forEach(p => {
      if (p.id !== id && group.includes(p.id)) p.open = false;
    });
  }

  panel.open = !panel.open;
}

// Unconditionally open a panel (as opposed to togglePanel, which flips
// whatever the current state is) — used when some other part of the app
// wants to make sure a specific panel is showing, e.g. after an explicit
// recompute so the user lands on the fit score.
function openPanel(id) {
  const panel = panels.value.find(p => p.id === id);
  if (!panel) return;

  const group = accordionGroupFor(id);
  if (group) {
    panels.value.forEach(p => {
      if (p.id !== id && group.includes(p.id)) p.open = false;
    });
  }

  panel.open = true;
}

const { panelRequest } = usePanelRequests();
watch(panelRequest, (id) => {
  if (!id) return;
  openPanel(id);
  panelRequest.value = null;
});
</script>

<template>
  <ErrorPopups />
  <ComputingIndicator />
  <LoginSyncIndicator />
  <AccountWidget>
    <div class="undo-redo">
      <button
        class="btn-copy"
        @click="undo()"
        :disabled="!canUndo"
        title="Undo last change to sight settings or measured marks"
      >
        ↶ Undo
      </button>
      <button
        class="btn-copy"
        @click="redo()"
        :disabled="!canRedo"
        title="Redo"
      >
        ↷ Redo
      </button>
    </div>

    <template #right>
      <SetupSelect />
    </template>
  </AccountWidget>

  <div class="layout">
    <!-- ─── Setup button bar ─── -->
    <div class="button-bar">
      <button
        v-for="p in setupPanels"
        :key="p.id"
        class="panel-button"
        :class="{ active: p.open }"
        @click="togglePanel(p.id)"
      >
        {{ p.title }} {{ p.open ? '▼' : '▶' }}
      </button>
    </div>

    <!-- ─── Open setup panel ─── -->
    <div class="open-panels">
      <Transition
        mode="out-in"
        @before-enter="beforeEnter"
        @enter="enter"
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
        @leave="leave"
        @after-leave="afterLeave"
      >
        <CollapsiblePanel
          v-if="openSetupPanel"
          :key="openSetupPanel.id"
          :title="openSetupPanel.title"
          :open="true"
          :full_width="openSetupPanel.full_width"
          @toggle="togglePanel(openSetupPanel.id)"
        >
          <component :is="panelComponents[openSetupPanel.id]" />
        </CollapsiblePanel>
      </Transition>
    </div>

    <!-- ─── Primary panel (always open, not toggleable) ───
         Keyed on the active setup: Compute Mark persists its own inputs
         (see mark.vue) scoped to whichever setup was active when it last
         mounted, so switching setups needs to remount it to pick up the
         newly active setup's own copy of that state. -->
    <div class="primary-panel" :key="activeSetupId">
      <CollapsiblePanel
        :title="primaryPanel.title"
        :open="true"
        :full_width="primaryPanel.full_width"
        fixed
      >
        <component :is="panelComponents[primaryPanel.id]" />
      </CollapsiblePanel>
    </div>

    <!-- ─── Reference button bar ─── -->
    <div class="button-bar">
      <button
        v-for="p in referencePanels"
        :key="p.id"
        class="panel-button"
        :class="{ active: p.open }"
        @click="togglePanel(p.id)"
      >
        {{ p.title }} {{ p.open ? '▼' : '▶' }}
      </button>
    </div>

    <!-- ─── Open reference panel ───
         Same remount-on-switch reasoning as .primary-panel above — Full
         Marks and Trajectory each persist their own per-setup inputs too. -->
    <div class="open-panels" :key="activeSetupId">
      <Transition
        mode="out-in"
        @before-enter="beforeEnter"
        @enter="enter"
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
        @leave="leave"
        @after-leave="afterLeave"
      >
        <CollapsiblePanel
          v-if="openReferencePanel"
          :key="openReferencePanel.id"
          :title="openReferencePanel.title"
          :open="true"
          :full_width="openReferencePanel.full_width"
          @toggle="togglePanel(openReferencePanel.id)"
        >
          <component :is="panelComponents[openReferencePanel.id]" />
        </CollapsiblePanel>
      </Transition>
    </div>
  </div>

  <div class="theme-picker">
    <label>Theme:
      <select v-model.string="currentTheme" @change="setTheme(currentTheme)">
        <option v-for="t in themes" :key="t.id" :value="t.id">
          {{ t.label }}
        </option>
      </select>
    </label>
  </div>
</template>

<style scoped>

/* ═══════════════════════════════════════════════
   Layout only — no panel styling here.
   All panel sizing is in collapsable_panel.vue
   ═══════════════════════════════════════════════ */

.layout {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  max-width: 100%;
  padding: 0 var(--space-sm);
  /* Extra room below the last panel so something opened near the bottom
     (e.g. a graph) can still be scrolled up to the vertical middle of the
     viewport, instead of being stuck flush against the end of the page. */
  padding-bottom: 50vh;
  box-sizing: border-box;
}

/* Button bars */
.button-bar {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: var(--space-sm);
}

.panel-button {
  padding: var(--space-sm) var(--space-md);
  background: var(--color-primary);
  color: var(--color-text-light);
  border: var(--border-width) solid transparent;
  border-radius: var(--border-radius);
  cursor: pointer;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  white-space: nowrap;
  box-sizing: border-box;
  transition: background 0.2s, border-color 0.2s;
}

.panel-button:hover {
  background: var(--color-primary-hover);
}

.panel-button.active {
  border-color: var(--color-border);
  background: var(--color-primary-hover);
}

/* Open panels (setup and reference) — vertical stack, centered */
.open-panels {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* Primary panel — centered, emphasized via border on the wrapper */
.primary-panel {
  display: flex;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
}

.primary-panel :deep(.panel) {
  border: var(--border-strong);
  --panel-border-width: 2px;
  box-shadow: var(--shadow-md);
}

/* Theme picker */
.theme-picker {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--space-sm) 0 var(--space-lg);
}

.undo-redo {
  display: flex;
  gap: var(--space-xs);
}

/* Neutral outline instead of .btn-copy's solid accent fill — that color
   is reserved for the active-setup selector (see setup_select.vue), the
   one thing in the top bar actually worth a glance; undo/redo are
   occasional actions, not the day-to-day focal point. Smaller than the
   other bar buttons for the same reason — they're now the bar's centered
   focal position (see account_widget.vue's .account-bar-center), which
   would otherwise make two secondary actions look more prominent than
   the setup selector next to them. */
.undo-redo .btn-copy {
  padding: var(--space-xs) var(--space-sm);
  font-size: var(--font-size-sm);
  background: var(--color-bg);
  color: var(--color-text);
  border: var(--border-width) solid var(--color-border);
}

.undo-redo .btn-copy:hover {
  background: var(--color-bg-alt);
  opacity: 1; /* overrides .btn-copy:hover's opacity dim, which reads oddly on a bordered/light fill */
}

.undo-redo .btn-copy:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.undo-redo .btn-copy:disabled:hover {
  opacity: 0.4;
  background: var(--color-bg);
}

</style>

